/**
 * The one authority on "is there a live session, and whose".
 *
 * Session state is DERIVED FROM TOKEN ACQUISITION SUCCEEDING, never from the
 * presence of an account in the MSAL cache. `useIsAuthenticated()` reports the
 * latter — it is literally `getAllAccounts().length > 0` — and the two diverge
 * every time an Entra SPA session ages out (ticket #33: the account outlives
 * the 24-hour refresh token by days, so cache presence claims a session that
 * cannot produce a single token). Believing the cache is what turned an
 * expected expiry into a login screen that refused to log anyone in.
 *
 * The state machine has one entry (`probe`) and six terminal-ish states, and
 * the router plus the sign-in screen read it instead of guessing:
 *
 *   checking      — MSAL is still handling a redirect, or /me is in flight
 *   authenticated — token acquired AND the backend returned a profile
 *   signedOut     — no account cached; first visit or after sign-out
 *   expired       — an account was cached but its session is over (evicted)
 *   unauthorized  — signed in with Microsoft, not provisioned in Tradeski (403)
 *   unavailable   — transient fault; the session may be alive, so retry
 *
 * `loading` and `unauthorized` remain on the context as derived views for the
 * consumers that only need the boolean.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { api, ApiError } from '../../api/client'
import type { UserMe } from '../../api/client'
import { acquireApiToken, endSession, loginRequest, onSessionEnded } from '../../api/msal'

export type SessionStatus =
    'checking' | 'authenticated' | 'signedOut' | 'expired' | 'unauthorized' | 'unavailable'

type AuthState = {
    /** The provisioned Tradeski profile, or null when signed out / not yet loaded. */
    user: UserMe | null
    /** Why there is (or isn't) a session — drives the sign-in screen's copy. */
    status: SessionStatus
    loading: boolean
    /** True when the caller authenticated with Microsoft but has no Tradeski
     *  account (backend 403) — an admin must provision them by email first. */
    unauthorized: boolean
    /** Re-run the session probe. Only meaningful from `unavailable`. */
    retry: () => void
    login: () => Promise<void>
    logout: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const { instance, inProgress } = useMsal()
    const [user, setUser] = useState<UserMe | null>(null)
    const [probedStatus, setProbedStatus] = useState<SessionStatus>('checking')
    // MSAL owning the window outranks whatever the last probe concluded: while
    // it works, the session is unknown. Derived rather than written into state
    // from an effect, so there is no render where a stale verdict shows.
    const status: SessionStatus = inProgress === InteractionStatus.None ? probedStatus : 'checking'
    // Monotonic probe id: a probe that has been superseded (by a retry, or by
    // session death reported from a hub) must not write stale state on return.
    const probeId = useRef(0)

    const probe = useCallback(async () => {
        const generation = ++probeId.current
        const settle = (next: SessionStatus, profile: UserMe | null = null) => {
            if (probeId.current !== generation) return
            setUser(profile)
            setProbedStatus(next)
        }

        const token = await acquireApiToken()
        if (token.kind === 'no-account') {
            settle('signedOut')
            return
        }
        if (token.kind === 'unavailable') {
            settle('unavailable')
            return
        }
        if (token.kind === 'interaction-required') {
            settle('expired')
            void endSession()
            return
        }

        try {
            settle('authenticated', await api.getMe())
        } catch (err) {
            if (!(err instanceof ApiError)) {
                settle('unavailable')
                return
            }
            // 403 = valid Microsoft sign-in, but not provisioned in Tradeski.
            if (err.status === 403) {
                settle('unauthorized')
                return
            }
            // 401 with a token in hand means the backend rejected it outright
            // (deprovisioned, app registration changed) — the session is over.
            if (err.status === 401) {
                settle('expired')
                void endSession()
                return
            }
            settle('unavailable')
        }
    }, [])

    // Session death found anywhere else — a background REST 401, a hub token
    // factory — lands here. Cancels any in-flight probe so it cannot resurrect
    // a session that is already gone.
    useEffect(
        () =>
            onSessionEnded(() => {
                probeId.current += 1
                setUser(null)
                setProbedStatus('expired')
            }),
        [],
    )

    // MSAL owns the window while it processes a redirect; probing mid-flight
    // would race its cache writes. `inProgress` reaches `None` once startup and
    // any redirect handling are done, and silent renewals never move it. While
    // it is busy there is nothing to do — `status` above already reads
    // 'checking' without anyone having to write it.
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return
        void probe()
    }, [inProgress, probe])

    const login = useCallback(async () => {
        await instance.loginRedirect(loginRequest)
    }, [instance])

    const logout = useCallback(() => {
        void instance.logoutRedirect()
    }, [instance])

    // Re-probing from a settled state has to show its work; the first probe
    // does not, because 'checking' is where this starts.
    const retry = useCallback(() => {
        setProbedStatus('checking')
        void probe()
    }, [probe])

    return (
        <AuthContext.Provider
            value={{
                user,
                status,
                loading: status === 'checking',
                unauthorized: status === 'unauthorized',
                retry,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used within AuthProvider')
    return ctx
}
